import "../Editor/editor.css";
import { AlignLeft, Bold, ImageIcon, Italic, LinkIcon } from "lucide-react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import MagicToast from "../../DesignSystem/MagicToast";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { getSingleFileFromInputEvent } from "../../../util/file";
import PurchaserClient from "../../../clients/PurchaserClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "../../../clients/ReactQueryClient";
import Tooltip from "../../DesignSystem/Tooltip";
import { Popover } from "../../DesignSystem";
import { createStore } from "../../../util/zustand";

const store = createStore({
    html: "",
    json: "",
});

export const useUserSignature = () => {
    const query = useQuery({
        queryKey: ["userSignature"],
        queryFn: async () => {
            // Get the existing user signature on the user object
            const res = await PurchaserClient.getCurrentUser();
            const userSignature = res.user?.emailSignature;

            if (userSignature) return userSignature;

            return "";
        },
    });

    return {
        existingSignature: query.data,
    };
};

const useSaveSignature = () => {
    const mutation = useMutation({
        mutationFn: async () => {
            const { html } = store.getState();

            // This is the empty default, so if they save this go ahead and clear the signature
            // since we don't have a "toggle off" yet
            if (html === "<p></p>" || html === "") {
                await PurchaserClient.clearEmailSignature();
            } else {
                await PurchaserClient.updateEmailSignature(html);
            }
        },
        async onSuccess() {
            // Reset the store and invalidate the query
            store.reset();
            await queryClient.invalidateQueries({ queryKey: ["userSignature"] });

            // Refetch the signature and update the store accordingly
            const res = await PurchaserClient.getCurrentUser();
            const userSignature = res.user?.emailSignature || "";
            store.update({ html: userSignature, json: "" });
        },
        onError(e) {
            console.error("Error saving signature", e);
        },
    });

    return {
        save: mutation.mutate,
        isSaving: mutation.isPending,
    };
};

const Component = (props: { inModal?: boolean; leftToolbarContent?: ReactNode }) => {
    const inModal = props.inModal === true;
    const { existingSignature } = useUserSignature();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
    const [linkText, setLinkText] = useState("");
    const [urlText, setUrlText] = useState("");
    const [linkErrorMessage, setLinkErrorMessage] = useState("");

    useEffect(() => {
        if (editor && existingSignature && existingSignature !== "") {
            editor.chain().clearContent().run();
            insertHTMLAtEndOfEditor(editor, existingSignature);
        }
    }, [existingSignature]);

    const editor = useEditor({
        extensions: [
            StarterKit,
            Link,
            Image.configure({
                allowBase64: true,
                resize: {
                    enabled: true,
                    directions: ["top-left", "top-right", "bottom-left", "bottom-right"],
                    minWidth: 50,
                    minHeight: 50,
                    alwaysPreserveAspectRatio: true,
                },
            }),
        ],
        shouldRerenderOnTransaction: false,
        content: existingSignature,
        onUpdate: ({ editor }) => {
            store.update({ html: editor.getHTML(), json: JSON.stringify(editor.getJSON()) });
        },
    });

    if (!editor) return null;

    function insertHTMLAtEndOfEditor(editor: Editor, htmlString: string) {
        const endPosition = editor.state.doc.content.size;
        const targetPosition = endPosition > 0 ? endPosition - 1 : 0;
        editor.chain().focus().insertContentAt(targetPosition, htmlString).run();
    }

    const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = getSingleFileFromInputEvent(e);
        if (!file) {
            return;
        }

        // Clear the input so another file can be selected
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

        const FIVE_MEGABYTES = 5_000_000;
        if (file.size > FIVE_MEGABYTES) {
            MagicToast.show("Image must be smaller than 5MB.");
            return;
        }

        if (editor.getHTML().includes("<img src")) {
            MagicToast.show("Only one image is supported.");
            return;
        }

        const { url } = await PurchaserClient.uploadEmailSignatureImage(file);

        insertHTMLAtEndOfEditor(editor, `<img src="${url}" />`);
    };

    const handleAddLink = () => {
        if (!urlText.startsWith("https://")) {
            setLinkErrorMessage(`URL must start with https://`);
            return;
        } else {
            setLinkErrorMessage("");
        }
        const content = `<a href="${urlText}" target="_blank">${linkText}</a>`;
        insertHTMLAtEndOfEditor(editor, content);
        setLinkText("");
        setUrlText("");
        setLinkPopoverOpen(false);
    };

    return (
        <div
            className={`flex max-w-full flex-col items-center justify-center overflow-hidden rounded-2xl border-stone-200 bg-white shadow ${inModal ? "" : "border"}`}
            onClick={e => {
                e.stopPropagation();
            }}
        >
            {/* Editor */}
            <div
                className={`w-full overflow-y-auto ${inModal ? "h-96" : "min-h-40"}`}
                onClick={() => {
                    editor.chain().focus();
                }}
            >
                <EditorContent editor={editor} className="h-full w-full p-4 break-all" />
            </div>

            {/* Toolbar */}
            <div className="flex h-16 w-full items-center justify-between border-t border-stone-200 p-4">
                <div className="flex min-w-fit">{props.leftToolbarContent}</div>

                <div className="flex w-full justify-end gap-1">
                    <Tooltip text="Bold" align="top" verticalOffset={-4}>
                        <div
                            className={`rounded p-2 ${editor.isActive("bold") ? "bg-stone-100 active:bg-stone-200" : "hover:bg-stone-100 active:bg-stone-100"}`}
                            onClick={() => editor.chain().focus().toggleBold().run()}
                        >
                            <Bold className="h-4 w-4 stroke-stone-600 group-hover:stroke-stone-900" />
                        </div>
                    </Tooltip>
                    <Tooltip text="Italic" align="top" verticalOffset={-4}>
                        <div
                            className={`rounded p-2 ${editor.isActive("italic") ? "bg-stone-100 active:bg-stone-200" : "hover:bg-stone-100 active:bg-stone-100"}`}
                            onClick={() => editor.chain().focus().toggleItalic().run()}
                        >
                            <Italic className="h-4 w-4 stroke-stone-600 group-hover:stroke-stone-900" />
                        </div>
                    </Tooltip>
                    <Tooltip text="Add Link" align="top" verticalOffset={-4} active={!linkPopoverOpen}>
                        <Popover
                            open={linkPopoverOpen}
                            onOpenChange={setLinkPopoverOpen}
                            className="w-64 items-center gap-2 p-4 text-stone-950"
                            trigger={
                                <button type="button" aria-label="Add Link" className="rounded p-2 hover:bg-stone-100">
                                    <LinkIcon className="h-4 w-4 stroke-stone-600 group-hover:stroke-stone-900" />
                                </button>
                            }
                        >
                            <div className="grid min-h-8 w-full grid-cols-1 rounded border border-stone-200 py-1">
                                <input
                                    value={linkText}
                                    onChange={e => setLinkText(e.currentTarget.value)}
                                    placeholder="Text"
                                    className="col-start-1 row-start-1 block w-full rounded-lg pl-10 text-base tracking-normal whitespace-normal [word-spacing:normal] focus:outline-none"
                                />
                                <AlignLeft className="pointer-events-none col-start-1 row-start-1 ml-3 h-4 w-4 self-center text-stone-400" />
                            </div>
                            <div className="grid min-h-8 w-full grid-cols-1 rounded border border-stone-200 py-1">
                                <input
                                    value={urlText}
                                    onChange={e => setUrlText(e.currentTarget.value)}
                                    onKeyDown={e => {
                                        if (e.key === "Enter") {
                                            handleAddLink();
                                        }
                                    }}
                                    placeholder="URL"
                                    className="col-start-1 row-start-1 block w-full rounded-lg pl-10 text-base focus:outline-none"
                                />
                                <LinkIcon className="pointer-events-none col-start-1 row-start-1 ml-3 h-4 w-4 self-center text-stone-400" />
                            </div>

                            <button
                                onClick={handleAddLink}
                                className="flex w-full items-center justify-center rounded border border-stone-200 bg-white px-2 py-1 text-base font-semibold whitespace-nowrap text-stone-950 hover:bg-stone-50 focus:outline-none"
                            >
                                Apply
                            </button>

                            {linkErrorMessage !== "" && <div className="text-red-400">{linkErrorMessage}</div>}
                        </Popover>
                    </Tooltip>

                    <Tooltip text="Add Image" align="top" verticalOffset={-4}>
                        <div
                            className="rounded p-2 hover:bg-stone-100"
                            onClick={() => {
                                fileInputRef.current?.click();
                            }}
                        >
                            <ImageIcon className="h-4 w-4 stroke-stone-600 group-hover:stroke-stone-900" />
                            {/* Hidden file input for attaching files */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={handleFileInputChange}
                                multiple={false}
                            />
                        </div>
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

export default {
    Component,
    useSaveSignature,
    store,
};
